import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface EditableNumberCellProps {
  /** Whole-dollar value the cell displays. We do the cents conversion at the
   *  edges so callers don't have to remember to / 100 in three places. */
  value: number;
  /** Called on blur / Enter when the value changed. Caller persists. */
  onCommit: (next: number) => void;
  className?: string;
  ariaLabel?: string;
  /** When true, render as a flat read-only number — used for totals rows. */
  readOnly?: boolean;
  /** Render nothing for zero in read-only mode (cleaner P&L tables). */
  blankZero?: boolean;
  /**
   * Display the formatted zero (`$0.00`, `0`, `0%`) instead of an empty cell
   * when the value is 0. Useful for grids where the operator wants every
   * cell to read as a real number rather than a blank — pairs with the
   * focus-select behaviour so the placeholder is easy to overwrite.
   */
  showZero?: boolean;
  /** USD by default. Pass `'pct'` for percentage cells (employer taxes, etc). */
  format?: 'currency' | 'plain' | 'pct';
}

/**
 * A grid-friendly numeric input. Behaves like a spreadsheet cell:
 *   * Tab / Enter / blur commit the value.
 *   * Esc reverts to the last server value.
 *   * Empty input commits as 0.
 *
 * Used by every editable projection grid (P&L projections, payroll, sales).
 */
export function EditableNumberCell({
  value,
  onCommit,
  className,
  ariaLabel,
  readOnly,
  blankZero,
  showZero,
  format = 'currency',
}: EditableNumberCellProps) {
  const [draft, setDraft] = useState<string>(formatForEdit(value, format, showZero));

  // Sync the local draft when the upstream value changes (e.g. server
  // refetch settles a different number). Without this the cell would
  // freeze on the first render's value.
  useEffect(() => {
    setDraft(formatForEdit(value, format, showZero));
  }, [value, format, showZero]);

  if (readOnly) {
    if (blankZero && value === 0) {
      return <span className={cn('tabular-nums text-muted-foreground/60', className)}>—</span>;
    }
    return <span className={cn('tabular-nums', className)}>{formatForDisplay(value, format)}</span>;
  }

  const commit = () => {
    const parsed = parseDraft(draft);
    if (Number.isFinite(parsed) && parsed !== value) {
      onCommit(parsed);
    } else {
      setDraft(formatForEdit(value, format, showZero));
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(formatForEdit(value, format, showZero));
          (e.target as HTMLInputElement).blur();
        }
      }}
      className={cn(
        'h-8 w-full rounded border border-transparent bg-transparent px-2 text-right text-sm tabular-nums',
        'hover:border-border focus:border-primary focus:bg-card focus:outline-none',
        blankZero && value === 0 && 'text-muted-foreground/60',
        showZero && value === 0 && 'text-muted-foreground/60',
        className,
      )}
    />
  );
}

function formatForDisplay(value: number, format: 'currency' | 'plain' | 'pct') {
  if (format === 'pct') return `${value.toFixed(2)}%`;
  if (format === 'plain') return value.toLocaleString('en-US');
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function formatForEdit(value: number, format: 'currency' | 'plain' | 'pct', showZero?: boolean) {
  if (value === 0) {
    if (!showZero) return '';
    if (format === 'pct') return '0%';
    // Currency cells render without `$` and without decimals to match the
    // non-zero formatting below — operators see `1,235` next to `0`, never
    // `$0.00` next to `1,235`.
    return '0';
  }
  if (format === 'pct') return value.toString();
  return value.toLocaleString('en-US', {
    maximumFractionDigits: format === 'currency' ? 0 : 2,
  });
}

function parseDraft(raw: string): number {
  const cleaned = raw.replace(/[$,_\s]/g, '');
  if (!cleaned) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}
